import { X, type LucideIcon } from 'lucide-react'
import { isValidElement, useRef, type FocusEvent, type KeyboardEvent, type ReactNode, type Ref } from 'react'
import { cn } from '../lib/cn'
import { createScanDetector } from '../lib/scan'
import { controlClass, FieldFrame, useFieldIds } from './Field'
import type { FieldProps, Size } from './types'

export interface InputProps extends FieldProps {
  value: string
  onChange: (value: string) => void
  type?: 'text' | 'search' | 'email' | 'tel' | 'url' | 'password'
  inputMode?: 'text' | 'numeric' | 'decimal' | 'search' | 'none'
  /** Beispiel, keine Anleitung */
  placeholder?: string
  size?: Size
  /** Icon oder Präfix im Feld */
  leading?: LucideIcon | ReactNode
  /** Suffix, Einheit, Schaltfläche */
  trailing?: ReactNode
  /** × bei Inhalt */
  clearable?: boolean
  /** Kennungen: EAN, Charge, Belegnummer */
  mono?: boolean
  selectOnFocus?: boolean
  maxLength?: number
  autoComplete?: string
  autoFocus?: boolean
  /** rechtsbündig mit Tabellenziffern (Zahlenfelder) */
  align?: 'start' | 'end'
  /** Enter oder Verlassen des Felds */
  onCommit?: (value: string) => void
  /** Scanner-Eingabe landet als Ganzes hier statt als Tastenfolge */
  onScan?: (code: string) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  ref?: Ref<HTMLInputElement>
  /** weitere aria-Attribute (Combobox) */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & Record<`data-${string}`, string | undefined>
}

const isIconComponent = (value: unknown): value is LucideIcon =>
  typeof value === 'function' ||
  (typeof value === 'object' && value !== null && !isValidElement(value) && '$$typeof' in value)

/** Einzeiliges Eingabefeld. Beschriftung steht über dem Feld, nie nur als Platzhalter. */
export function Input({
  value,
  onChange,
  type = 'text',
  inputMode,
  placeholder,
  size = 'md',
  leading,
  trailing,
  clearable,
  mono,
  selectOnFocus,
  maxLength,
  autoComplete = 'off',
  autoFocus,
  align = 'start',
  onCommit,
  onScan,
  onFocus,
  onBlur,
  onKeyDown,
  ref,
  inputProps,
  label,
  labelHidden,
  hint,
  error,
  required,
  disabled,
  readOnly,
  id,
  name,
  className,
  'data-testid': testId,
}: InputProps) {
  const ids = useFieldIds({ id, hint, error })
  const framed = Boolean(label || hint || error)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const committed = useRef(value)
  const detector = useRef(onScan ? createScanDetector() : null)
  const scanStart = useRef<string | null>(null)

  const commit = (next: string): void => {
    if (next === committed.current) return
    committed.current = next
    onCommit?.(next)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (onScan && detector.current) {
      // Wert vor Beginn einer möglichen Scanner-Folge merken, um ihn danach wiederherzustellen
      if (detector.current.pending === 0) scanStart.current = event.currentTarget.value
      const code = detector.current.push(event.key, event.timeStamp || Date.now())
      if (code !== null) {
        event.preventDefault()
        onChange(scanStart.current ?? '')
        onScan(code)
        return
      }
    }
    onKeyDown?.(event)
    if (event.defaultPrevented) return
    if (event.key === 'Enter') commit(event.currentTarget.value)
  }

  const Leading = leading
  const field = (
    <div
      className={cn(controlClass({ invalid: ids.invalid, disabled, readOnly, size }), !framed && className)}
      data-ui="field"
      onMouseDown={(event) => {
        // Klick auf Rand oder Icon setzt den Fokus ins Feld
        if (event.target === event.currentTarget) {
          event.preventDefault()
          inputRef.current?.focus()
        }
      }}
    >
      {Leading !== undefined && Leading !== null && (
        <span className="flex shrink-0 items-center pl-2.5 text-fg-subtle touch:pl-3">
          {isIconComponent(Leading) ? (
            <Leading className="size-4 touch:size-5" strokeWidth={1.75} aria-hidden />
          ) : (
            Leading
          )}
        </span>
      )}
      <input
        {...inputProps}
        ref={(node) => {
          inputRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        id={ids.id}
        name={name}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        aria-label={!label ? (inputProps?.['aria-label'] ?? placeholder) : undefined}
        aria-invalid={ids.invalid || undefined}
        aria-describedby={ids.describedBy}
        data-testid={testId ?? (inputProps as { 'data-testid'?: string } | undefined)?.['data-testid']}
        data-scan-target={onScan ? '' : undefined}
        spellCheck={mono ? false : undefined}
        className={cn(
          'h-full w-full min-w-0 flex-1 bg-transparent px-(--control-px) outline-none placeholder:text-fg-subtle disabled:cursor-not-allowed',
          Leading !== undefined && Leading !== null && 'pl-2',
          // Mono ist 0,95 em – auf Touch bleibt es bei 16 px, sonst zoomt iOS ins Feld (§6.2)
          mono && 'code-id touch:text-touch',
          align === 'end' && 'num',
        )}
        onChange={(event) => onChange(event.target.value)}
        onFocus={(event) => {
          committed.current = event.target.value
          if (selectOnFocus) event.target.select()
          onFocus?.(event)
        }}
        onBlur={(event) => {
          commit(event.target.value)
          onBlur?.(event)
        }}
        onKeyDown={handleKeyDown}
      />
      {clearable && value !== '' && !disabled && !readOnly && (
        <button
          type="button"
          aria-label="Eingabe löschen"
          className="mr-1 flex size-6 shrink-0 items-center justify-center rounded-xs text-fg-subtle hit-area hover:bg-hover hover:text-fg"
          onClick={() => {
            onChange('')
            inputRef.current?.focus()
          }}
        >
          <X className="size-3.5" strokeWidth={2} aria-hidden />
        </button>
      )}
      {trailing !== undefined && trailing !== null && (
        <span className="flex shrink-0 items-center pr-2.5 text-fg-subtle touch:pr-3">{trailing}</span>
      )}
    </div>
  )

  if (!framed) return field
  return (
    <FieldFrame
      ids={ids}
      label={label}
      labelHidden={labelHidden}
      hint={hint}
      error={error}
      required={required}
      className={className}
    >
      {field}
    </FieldFrame>
  )
}
